import React, { useEffect, useState, forwardRef } from 'react'
import { Box, Icon } from '@maui/elements'
import { useDraft } from '@maui/hooks'
import { Details, Term, Definition } from '@/components/common/Details'
import { Divider } from '@/components/common/Divider'
import { UpgradeGroup } from '@/components/common/Upgrade/UpgradeGroup'
import { IconListGrid, ListItemLink, NameWithSeenTooltipIcon } from '../../common/IconListGrid'
import { DetailsSection } from '../DetailsSection'

const AssociationDetails = forwardRef(({ actorData, expanded: { malwareList }, expandedUpdated }, ref) => {
  const [showMalware, setShowMalware] = useDraft(malwareList)
  const [showTools, setShowTools] = useState(false)
  const [showVulnerabilities, setShowVulnerabilities] = useState(false)

  useEffect(() => {
    if (malwareList) setShowMalware(true)
  }, [expandedUpdated, malwareList, setShowMalware])

  const { malware, cve, tools } = actorData

  const isFreemiumUser = Object.values(actorData).some(
    (value) => value === 'redacted',
  )

  if (malware?.length === 0 &&
    cve?.length === 0 &&
    tools?.length === 0) {
    return (
      <Box ref={ref}>
        <Divider />
        <DetailsSection ref={ref} title="Associations">
          <Details>
            There are no known malwares, tools, or vulnerabilities associations at this time.
          </Details>
        </DetailsSection>
      </Box>
    )
  }

  return (
    <DetailsSection title="Associations">
      <Box ref={ref}>
        <Details>
          {isFreemiumUser
            ? (
              <UpgradeGroup
                layout='row'
                description="In order to view this content, you have to update to Premium. You're currently in the Freemium subscription, and you're missing a lot!"
                titleIcon='list'
                title='Actor Associations'
                height='100%'
              >
                <>
                  <Term>Malware</Term>
                  <Term>Tools</Term>
                  <Term>Vulnerabilities</Term>
                </>
              </UpgradeGroup>
              )
            : (
              <>
                <Term marginTop='8px'>Malware</Term>
                <Definition marginTop='8px'>
                  {malware && Array.isArray(malware) &&
                    <IconListGrid
                      list={malware}
                      showItems={showMalware}
                      setShowItems={setShowMalware}
                      type='malware'
                      displayLimit={12}
                      cellWidths={['100%', '50%', '33.33%', '25%']}
                      emptyMessage='There are no known malware associations at this time.'
                      renderItem={(item) => (
                        <ListItemLink to={`/malware/${item.id}`}>
                          <NameWithSeenTooltipIcon
                            iconName='bug'
                            item={item}
                            type={item.name}
                            iconComponent={Icon}
                            flagProps={{ name: 'bug' }}
                          />
                        </ListItemLink>
                      )}
                    />}
                </Definition>

                <Term>Tools</Term>
                <Definition>
                  {tools && Array.isArray(tools) &&
                    <IconListGrid
                      list={tools}
                      showItems={showTools}
                      setShowItems={setShowTools}
                      type='tool'
                      displayLimit={12}
                      emptyMessage='There are no known tool associations at this time.'
                      renderItem={(item) => (
                        <ListItemLink to={`/malware/${item.id}`}>
                          <Icon name='tools' /> {item.name}
                        </ListItemLink>
                      )}
                    />}
                </Definition>

                <Term>Vulnerabilities</Term>
                <Definition>
                  {cve && Array.isArray(cve) &&
                    <IconListGrid
                      list={cve}
                      showItems={showVulnerabilities}
                      setShowItems={setShowVulnerabilities}
                      type='cve'
                      displayLimit={12}
                      cellWidths={['100%', '50%', '33.33%', '25%']}
                      emptyMessage='There are no known vulnerability associations at this time.'
                      renderItem={(item) => (
                        <ListItemLink to={`/cve/${item.id}`}>
                          <Icon name='shield-alert' />
                          {item.cve_id}
                        </ListItemLink>
                      )}
                    />}
                </Definition>
              </>
              )}
        </Details>
      </Box>
    </DetailsSection>
  )
})

AssociationDetails.displayName = 'AssociationDetails'

export default AssociationDetails
